import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import awsConfig from "../aws-config";
import { usePageMetadata } from "../hooks/usePageMetadata";
import "./SettingsPage.css";
import { resolveAuthToken } from "../utils/authToken";
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from "../utils/currency";
import { optimiseImageForUpload } from "../utils/imageUpload";
import UpgradePlanSelector from "../components/UpgradePlanSelector";
import { useLifetimeUpgradePlans } from "../hooks/useLifetimeUpgradePlans";

const BUSINESS_API_URL = awsConfig.api_url_create_business;
const PRESIGN_API = awsConfig.api_url_presign_logo;
const UPGRADE_API_URL = awsConfig.api_url_upgrade;

const FONT_OPTIONS = [
  { label: "Montserrat", value: "Montserrat" },
  { label: "Inter", value: "Inter" },
  { label: "Roboto", value: "Roboto" },
  { label: "Open Sans", value: "Open Sans" },
  { label: "Lato", value: "Lato" },
  { label: "Poppins", value: "Poppins" },
];

const DEFAULT_TIME_ZONE = "Europe/London";
const FALLBACK_TIMEZONES = [
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Warsaw",
  "Europe/Athens",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Vancouver",
  "America/Toronto",
  "America/Sao_Paulo",
  "America/Mexico_City",
  "Africa/Johannesburg",
  "Africa/Cairo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
];
const TIMEZONE_OPTIONS = Array.from(
  new Set(
    ((typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : FALLBACK_TIMEZONES) || []
    ).concat(DEFAULT_TIME_ZONE)
  )
  ).sort();
const MAX_LOGO_SIZE = 200 * 1024; // 200 KB
const MAX_IMAGE_INPUT_BYTES = 8 * 1024 * 1024; // 8 MB source image
const MAX_PDF_SIZE = 200 * 1024; // 200 KB
const DEFAULT_CURRENCY_CODE = "GBP";
const SHOW_BOOKING_BETA = false;

function normaliseAssetUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    const host = (parsed.hostname || "").toLowerCase();
    if (host === "assets.brandmykitchen.com" || host === "dev-assets.brandmykitchen.com") {
      return raw;
    }
    if (host.endsWith(".s3.amazonaws.com") && parsed.pathname) {
      const assetHost =
        typeof window !== "undefined" && window.location.hostname === "app.brandmykitchen.com"
          ? "https://assets.brandmykitchen.com"
          : "https://dev-assets.brandmykitchen.com";
      return `${assetHost}${parsed.pathname}`;
    }
    return raw;
  } catch (_err) {
    return raw;
  }
}

export default function SettingsPage() {
  usePageMetadata({
    title: "Settings",
    description: "Update your business details, colours, and store publishing preferences.",
  });

  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressPostcode: "",
    addressCountry: "United Kingdom",
    timeZone: DEFAULT_TIME_ZONE,
    openingHours: {},
    primaryColor: "#2563eb",
    secondaryColor: "#1e3a8a",
    businessFont: "Montserrat",
    currencyCode: DEFAULT_CURRENCY_CODE,
  });
  const [bookingSchedules, setBookingSchedules] = useState([]);
  const blankSchedule = useMemo(
    () => ({
      scheduleId: "",
      name: "",
      resourceId: "",
      resourceLabel: "",
      timezone: DEFAULT_TIME_ZONE,
      startDate: "",
      endDate: "",
      daysOfWeek: [1, 2, 3, 4, 5],
      dailyStartTime: "09:00",
      dailyEndTime: "17:00",
      slotDurationMinutes: 60,
      intervalMinutes: 60,
      bufferMinutes: 0,
      maxBookings: 1,
      price: { amount: "", currencyCode: DEFAULT_CURRENCY_CODE },
      blackoutDates: [],
      notes: "",
      enabled: true,
    }),
    []
  );
  const [scheduleDraft, setScheduleDraft] = useState(blankSchedule);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
const [logoFile, setLogoFile] = useState(null);
const [logoPreview, setLogoPreview] = useState("");
const [logoUrl, setLogoUrl] = useState("");
const [pdfFile, setPdfFile] = useState(null);
const [pdfUrl, setPdfUrl] = useState("");
const [pdfViewUrl, setPdfViewUrl] = useState("");
const [imageUrls, setImageUrls] = useState([]);
const [heroImageUrl, setHeroImageUrl] = useState("");
const [venueImageUrl, setVenueImageUrl] = useState("");
const [heroImagePreview, setHeroImagePreview] = useState("");
const [venueImagePreview, setVenueImagePreview] = useState("");
const [venueName, setVenueName] = useState("");
const [mapUrl, setMapUrl] = useState("");
const [imageBusy, setImageBusy] = useState(false);
const [heroImageBusy, setHeroImageBusy] = useState(false);
const [venueImageBusy, setVenueImageBusy] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState(SUPPORTED_CURRENCIES);
  const {
    saleWindow,
    countdownLabel,
    availablePlans,
    selectedPlanId,
    setSelectedPlanId,
    selectedPlan,
  } = useLifetimeUpgradePlans();
  const saleStatusCopy = useMemo(() => {
    if (saleWindow.phase === "active") {
      return countdownLabel
        ? `Black Friday Lifetime Deal is live – ${countdownLabel} left.`
        : "Black Friday Lifetime Deal is live.";
    }
    if (saleWindow.phase === "upcoming") {
      const startLabel = saleWindow.start
        ? saleWindow.start.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : null;
      return startLabel ? `Lifetime Deal unlocks ${startLabel}.` : "Lifetime Deal unlocks soon.";
    }
    if (saleWindow.phase === "ended") {
      return "Lifetime Deal has ended. Starter plan is still available monthly.";
    }
    return "Upgrade unlocks more orders, stores, and white-label controls.";
  }, [saleWindow.phase, saleWindow.start, countdownLabel]);
  const [upgrading, setUpgrading] = useState(false);

  const timezoneOptions = useMemo(() => {
    const base = new Set(TIMEZONE_OPTIONS);
    if (form.timeZone) {
      base.add(form.timeZone);
    }
    return Array.from(base).sort();
  }, [form.timeZone]);
  const currencySymbol = useMemo(
    () => getCurrencySymbol(form.currencyCode || DEFAULT_CURRENCY_CODE, currencyOptions),
    [form.currencyCode, currencyOptions]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [stripeLoginLoading, setStripeLoginLoading] = useState(false);

  const getAuthToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = resolveAuthToken(session);
    if (!token) throw new Error("Not authenticated");
    return token;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("upgraded") === "1") {
      setShowBanner(true);
      params.delete("upgraded");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const fetchBusiness = async () => {
      setLoading(true);
      setError("");
      try {
        const userObj = await getCurrentUser().catch(() => null);
        if (!userObj) {
          navigate("/auth");
          return;
        }
        const token = await getAuthToken();
        const res = await fetch(BUSINESS_API_URL, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch business info");
        const data = await res.json();
        const normalizedBusiness = {
          ...data,
          onboardingComplete: data.onboardingComplete !== false,
          timeZone: data.timeZone || DEFAULT_TIME_ZONE,
        };
        setBusiness(normalizedBusiness);
        const address = normalizedBusiness.businessAddress || {};
        setForm({
          name: normalizedBusiness.name || "",
          email: normalizedBusiness.email || "",
          phone: "",
          addressLine1: address.street || "",
          addressLine2: address.line2 || "",
          addressCity: address.city || "",
          addressPostcode: address.postcode || "",
          addressCountry: address.country || "United Kingdom",
          openingHours: normalizedBusiness.openingHours || {},
          timeZone: normalizedBusiness.timeZone || DEFAULT_TIME_ZONE,
          primaryColor: normalizedBusiness.primaryColor || "#2563eb",
          secondaryColor: normalizedBusiness.secondaryColor || "#1e3a8a",
          businessFont: normalizedBusiness.businessFont || "Montserrat",
          currencyCode: normalizedBusiness.currencyCode || DEFAULT_CURRENCY_CODE,
        });
        if (Array.isArray(normalizedBusiness.supportedCurrencies) && normalizedBusiness.supportedCurrencies.length > 0) {
          setCurrencyOptions(normalizedBusiness.supportedCurrencies);
        } else {
          setCurrencyOptions(SUPPORTED_CURRENCIES);
        }
        setLogoUrl(normaliseAssetUrl(normalizedBusiness.logoUrl || ""));
        setPdfUrl(normalizedBusiness.digitalProductUrl || normalizedBusiness.pdfUrl || "");
        setPdfViewUrl("");
        setImageUrls(Array.isArray(normalizedBusiness.imageUrls) ? normalizedBusiness.imageUrls.map((entry) => normaliseAssetUrl(entry)).filter(Boolean) : []);
        setHeroImageUrl(normaliseAssetUrl(normalizedBusiness.heroImage || ""));
        setVenueImageUrl(normaliseAssetUrl(normalizedBusiness.venueImage || ""));
        setHeroImagePreview("");
        setVenueImagePreview("");
        setVenueName(normalizedBusiness.venueName || "");
        setMapUrl(normalizedBusiness.mapUrl || "");
        if (Array.isArray(normalizedBusiness.bookingSchedules)) {
          setBookingSchedules(
            normalizedBusiness.bookingSchedules.map((s) => ({
              ...blankSchedule,
              ...s,
              price: {
                amount: s?.price?.amount ?? "",
                currencyCode:
                  s?.price?.currencyCode ||
                  s?.currencyCode ||
                  normalizedBusiness.currencyCode ||
                  DEFAULT_CURRENCY_CODE,
              },
              blackoutDates: Array.isArray(s?.blackoutDates) ? s.blackoutDates : [],
              daysOfWeek: Array.isArray(s?.daysOfWeek) ? s.daysOfWeek : [],
              enabled: s?.enabled !== false,
            }))
          );
        } else {
          setBookingSchedules([]);
        }
        setScheduleDraft((draft) => ({
          ...draft,
          timezone: normalizedBusiness.timeZone || DEFAULT_TIME_ZONE,
          price: {
            ...draft.price,
            currencyCode: normalizedBusiness.currencyCode || DEFAULT_CURRENCY_CODE,
          },
        }));
      } catch {
        setError("Could not load business info.");
      } finally {
        setLoading(false);
      }
    };
    fetchBusiness();
  }, [getAuthToken, navigate, blankSchedule]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_LOGO_SIZE) {
        setError("Logo must be smaller than 200 KB.");
        setLogoFile(null);
        setLogoPreview("");
        return;
      }
      setError("");
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview("");
    }
  };

  const uploadLogo = async () => {
    if (!logoFile || !business?.businessId) return "";
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
          logoFile.name
        )}&contentType=${encodeURIComponent(logoFile.type)}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        let errorText = "";
        try {
          errorText = await res.text();
        } catch (_err) {
          // ignore
        }
        throw new Error(
          errorText ? `Failed to get presigned URL: ${errorText}` : "Failed to get presigned URL"
        );
      }
      const { uploadUrl, publicUrl } = await res.json();
      if (!uploadUrl) {
        throw new Error("Presign response missing uploadUrl");
      }
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": logoFile.type },
        body: logoFile,
      });
      if (!uploadRes.ok) {
        throw new Error(`Failed to upload logo to S3 (${uploadRes.status})`);
      }
      const normalizedUrl = normaliseAssetUrl(publicUrl);
      setLogoUrl(normalizedUrl);
      return normalizedUrl;
    } catch (err) {
      console.error("Logo upload error:", err);
      setError("Could not upload logo.");
      return "";
    }
  };

  const fetchPdfViewUrl = useCallback(
    async (fileNameOverride) => {
      if (!business?.businessId || !(pdfUrl || fileNameOverride)) return;
      const fileName = fileNameOverride || decodeURIComponent((pdfUrl.split("/").pop() || "").split("?")[0]);
      if (!fileName) return;
      try {
        const token = await getAuthToken();
        const res = await fetch(
          `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
            fileName
          )}&contentType=${encodeURIComponent("application/pdf")}&mode=get`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.getUrl) {
            setPdfViewUrl(data.getUrl);
          }
        }
      } catch (err) {
        console.error("PDF view presign error:", err);
      }
    },
    [business?.businessId, getAuthToken, pdfUrl]
  );

  useEffect(() => {
    if (pdfUrl && business?.businessId) {
      fetchPdfViewUrl();
    }
  }, [pdfUrl, business?.businessId, fetchPdfViewUrl]);

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        setError("Please upload a PDF file.");
        setPdfFile(null);
        return;
      }
      if (file.size > MAX_PDF_SIZE) {
        setError("PDF must be smaller than 200 KB.");
        setPdfFile(null);
        return;
      }
      setError("");
      setPdfFile(file);
    } else {
      setPdfFile(null);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 3 - imageUrls.length;
    const queue = files.slice(0, remaining);
    queue.forEach((file) => {
      if (file.size > MAX_IMAGE_INPUT_BYTES) {
        setError("Images must be smaller than 8 MB before processing.");
        return;
      }
      if (!(file.type || "").startsWith("image/")) {
        setError("Please upload image files only.");
        return;
      }
      uploadImage(file);
    });
    e.target.value = "";
  };

  const uploadPdf = async () => {
    if (!pdfFile || !business?.businessId) return "";
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
          pdfFile.name
        )}&contentType=${encodeURIComponent(pdfFile.type || "application/pdf")}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        let errorText = "";
        try {
          errorText = await res.text();
        } catch (_err) {
          // ignore
        }
        throw new Error(
          errorText ? `Failed to get presigned URL: ${errorText}` : "Failed to get presigned URL"
        );
      }
      const { uploadUrl, publicUrl, getUrl } = await res.json();
      if (!uploadUrl) {
        throw new Error("Presign response missing uploadUrl");
      }
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": pdfFile.type || "application/pdf" },
        body: pdfFile,
      });
      if (!uploadRes.ok) {
        throw new Error(`Failed to upload PDF to S3 (${uploadRes.status})`);
      }
      setPdfUrl(publicUrl);
      if (getUrl) {
        setPdfViewUrl(getUrl);
      }
      return publicUrl;
    } catch (err) {
      console.error("PDF upload error:", err);
      setError("Could not upload PDF.");
      return "";
    }
  };

  const uploadImage = async (file) => {
    if (!file || !business?.businessId) return "";
    if (!(file.type || "").startsWith("image/")) {
      setError("Please upload an image file.");
      return "";
    }
    if (file.size > MAX_IMAGE_INPUT_BYTES) {
      setError("Images must be smaller than 8 MB before processing.");
      return "";
    }
    setImageBusy(true);
    try {
      const processedFile = await optimiseImageForUpload(file, {
        maxInputBytes: MAX_IMAGE_INPUT_BYTES,
        maxDimension: 1800,
        outputType: "image/jpeg",
        targetBytes: 900 * 1024,
      });
      const token = await getAuthToken();
      const res = await fetch(
        `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
          `images/${processedFile.name}`
        )}&contentType=${encodeURIComponent(processedFile.type || "image/jpeg")}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        let errorText = "";
        try {
          errorText = await res.text();
        } catch (_err) {
          // ignore
        }
        throw new Error(
          errorText ? `Failed to get presigned URL: ${errorText}` : "Failed to get presigned URL"
        );
      }
      const { uploadUrl, publicUrl } = await res.json();
      if (!uploadUrl) {
        throw new Error("Presign response missing uploadUrl");
      }
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": processedFile.type || "image/jpeg" },
        body: processedFile,
      });
      if (!uploadRes.ok) {
        throw new Error(`Failed to upload image to S3 (${uploadRes.status})`);
      }
      setImageUrls((prev) => {
        const next = [...prev, normaliseAssetUrl(publicUrl)].slice(0, 3);
        return next;
      });
      return normaliseAssetUrl(publicUrl);
    } catch (err) {
      console.error("Image upload error:", err);
      setError("Could not upload image.");
      return "";
    } finally {
      setImageBusy(false);
    }
  };

  const uploadDedicatedImage = async (file, keyPrefix = "booking-image") => {
    if (!file || !business?.businessId) return "";
    if (!(file.type || "").startsWith("image/")) {
      setError("Please upload an image file.");
      return "";
    }
    if (file.size > MAX_IMAGE_INPUT_BYTES) {
      setError("Images must be smaller than 8 MB before processing.");
      return "";
    }
    try {
      const processedFile = await optimiseImageForUpload(file, {
        maxInputBytes: MAX_IMAGE_INPUT_BYTES,
        maxDimension: 1800,
        outputType: "image/jpeg",
        targetBytes: 500 * 1024,
      });
      const uploadName = `${keyPrefix}-${processedFile.name}`.replace(/[^A-Za-z0-9._/-]+/g, "-");
      const token = await getAuthToken();
      const res = await fetch(
        `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
          `images/${uploadName}`
        )}&contentType=${encodeURIComponent(processedFile.type || "image/jpeg")}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        let errorText = "";
        try {
          errorText = await res.text();
        } catch (_err) {
          // ignore
        }
        throw new Error(
          errorText ? `Failed to get presigned URL: ${errorText}` : "Failed to get presigned URL"
        );
      }
      const { uploadUrl, publicUrl } = await res.json();
      if (!uploadUrl) throw new Error("Presign response missing uploadUrl");
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": processedFile.type || "image/jpeg" },
        body: processedFile,
      });
      if (!uploadRes.ok) {
        throw new Error(`Failed to upload image to S3 (${uploadRes.status})`);
      }
      setError("");
      return normaliseAssetUrl(publicUrl || "");
    } catch (err) {
      console.error("Dedicated image upload error:", err);
      setError(err?.message || "Could not upload image.");
      return "";
    }
  };

  const handleDedicatedImageChange = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const localPreview = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Could not read the image."));
      reader.readAsDataURL(file);
    }).catch((err) => {
      setError(err?.message || "Could not read the image.");
      return "";
    });
    if (!localPreview) return;
    if (kind === "hero") {
      setHeroImageBusy(true);
      setHeroImagePreview(localPreview);
      const nextUrl = await uploadDedicatedImage(file, "hero");
      if (nextUrl) {
        setHeroImageUrl(nextUrl);
      } else {
        setHeroImagePreview("");
      }
      setHeroImageBusy(false);
      return;
    }
    setVenueImageBusy(true);
    setVenueImagePreview(localPreview);
    const nextUrl = await uploadDedicatedImage(file, "venue");
    if (nextUrl) {
      setVenueImageUrl(nextUrl);
    } else {
      setVenueImagePreview("");
    }
    setVenueImageBusy(false);
  };

  const updateScheduleDraft = (field, value) => {
    setScheduleDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleDay = (day) => {
    setScheduleDraft((prev) => {
      const hasDay = prev.daysOfWeek.includes(day);
      const days = hasDay ? prev.daysOfWeek.filter((d) => d !== day) : [...prev.daysOfWeek, day];
      return { ...prev, daysOfWeek: days.sort((a, b) => a - b) };
    });
  };

  const saveScheduleDraft = () => {
    // Basic validation
    if (!scheduleDraft.name.trim()) {
      setError("Please add a schedule name/resource label.");
      return;
    }
    if (!scheduleDraft.dailyStartTime || !scheduleDraft.dailyEndTime) {
      setError("Please provide start and end time.");
      return;
    }
    setError("");
    const scheduleId = editingScheduleId || scheduleDraft.scheduleId || crypto.randomUUID();
    const normalized = {
      ...scheduleDraft,
      scheduleId,
      resourceId: scheduleDraft.resourceId || scheduleId,
      resourceLabel: scheduleDraft.resourceLabel || scheduleDraft.name,
      price: {
        amount: scheduleDraft.price?.amount || "",
        currencyCode: scheduleDraft.price?.currencyCode || form.currencyCode || DEFAULT_CURRENCY_CODE,
      },
      blackoutDates: (scheduleDraft.blackoutDates || []).filter(Boolean),
    };
    setBookingSchedules((prev) => {
      const others = prev.filter((s) => s.scheduleId !== scheduleId);
      return [...others, normalized];
    });
    setEditingScheduleId(null);
    setScheduleDraft({
      ...blankSchedule,
      timezone: form.timeZone || DEFAULT_TIME_ZONE,
      price: { ...blankSchedule.price, currencyCode: form.currencyCode || DEFAULT_CURRENCY_CODE },
    });
  };

  const editSchedule = (id) => {
    const found = bookingSchedules.find((s) => s.scheduleId === id);
    if (!found) return;
    setEditingScheduleId(id);
    setScheduleDraft({
      ...blankSchedule,
      ...found,
      blackoutDates: Array.isArray(found.blackoutDates) ? found.blackoutDates : [],
      daysOfWeek: Array.isArray(found.daysOfWeek) ? found.daysOfWeek : [],
      price: {
        amount: found?.price?.amount || "",
        currencyCode: found?.price?.currencyCode || form.currencyCode || DEFAULT_CURRENCY_CODE,
      },
    });
  };

  const deleteSchedule = (id) => {
    setBookingSchedules((prev) => prev.filter((s) => s.scheduleId !== id));
    if (editingScheduleId === id) {
      setEditingScheduleId(null);
      setScheduleDraft({
        ...blankSchedule,
        timezone: form.timeZone || DEFAULT_TIME_ZONE,
        price: { ...blankSchedule.price, currencyCode: form.currencyCode || DEFAULT_CURRENCY_CODE },
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (!form.addressLine1.trim() || !form.addressCity.trim() || !form.addressPostcode.trim() || !form.addressCountry.trim()) {
        throw new Error("Please complete your business address before saving.");
      }
      const token = await getAuthToken();

      let finalLogoUrl = logoUrl;
      if (logoFile) {
        finalLogoUrl = await uploadLogo();
        if (!finalLogoUrl) throw new Error("Logo upload failed");
      }

      let finalPdfUrl = pdfUrl;
      if (pdfFile) {
        finalPdfUrl = await uploadPdf();
        if (!finalPdfUrl) throw new Error("PDF upload failed");
      }

      const formData = new FormData();
      formData.append("businessId", business.businessId);
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", "");
      formData.append("logoUrl", finalLogoUrl);
      formData.append("openingHours", JSON.stringify(form.openingHours));
      formData.append("timeZone", form.timeZone || DEFAULT_TIME_ZONE);
      formData.append("primaryColor", form.primaryColor);
      formData.append("secondaryColor", form.secondaryColor);
      formData.append("businessFont", form.businessFont);
      formData.append("addressLine1", form.addressLine1.trim());
      formData.append("addressLine2", form.addressLine2.trim());
      formData.append("addressCity", form.addressCity.trim());
      formData.append("addressPostcode", form.addressPostcode.trim());
      formData.append("addressCountry", (form.addressCountry || "United Kingdom").trim());
      formData.append("currencyCode", (form.currencyCode || DEFAULT_CURRENCY_CODE).trim());
      formData.append("digitalProductUrl", finalPdfUrl || "");
      formData.append("pdfUrl", finalPdfUrl || "");
      formData.append("imageUrls", JSON.stringify(imageUrls || []));
      formData.append("heroImage", heroImageUrl || "");
      formData.append("venueImage", venueImageUrl || "");
      formData.append("venueName", venueName || "");
      formData.append("mapUrl", mapUrl || "");
      // Booking schedules are managed via the Home page flow; omit here to avoid duplicate writes.
      
      // 🔥 THE FIX - Preserve the current plan!
      formData.append("planType", business.plan || business.planType || "free");
      formData.append("onboardingComplete", business?.onboardingComplete === false ? "false" : "true");

      const res = await fetch(BUSINESS_API_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to save changes");
      alert("Settings saved!");
      setLogoFile(null);
      setLogoPreview("");
      setPdfFile(null);
    } catch (err) {
      setError(err?.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async (planIdOverride) => {
    if (!business?.businessId) return;
    const planId = planIdOverride || selectedPlanId;
    if (!planId) return;
    setUpgrading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(UPGRADE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: business.businessId,
          email: form.email,
          planId,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Could not start upgrade. Please try again.");
      }
    } catch {
      alert("Upgrade failed. Please try again.");
    } finally {
      setUpgrading(false);
    }
  };

  if (loading)
    return (
      <div className="settings-shell">
        <div className="settings-container">Loading…</div>
      </div>
    );
  if (error)
    return (
      <div className="settings-shell">
        <div className="settings-container error">{error}</div>
      </div>
    );

  return (
    <div className="settings-shell">
      <div className="settings-container">
        <button
          type="button"
          className="settings-back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <h2>Settings</h2>
        <div className="settings-integrations-callout">
          <span>Need API keys for embeds?</span>
          <Link to="/integrations">Open Integrations</Link>
        </div>

        {showBanner && (
          <div className="settings-banner">🎉 Your plan has been upgraded to Starter!</div>
        )}

        {(!business?.plan || business.plan === "free") && (
          <div className="upgrade-section" style={{ marginBottom: 24, textAlign: "center" }}>
            <h3 style={{ marginBottom: 8 }}>Unlock Lifetime Upgrades</h3>
          <p style={{ marginBottom: 16 }}>{saleStatusCopy}</p>
          <UpgradePlanSelector
            className="settings-plan-selector"
            plans={availablePlans}
            selectedPlanId={selectedPlanId}
            onSelect={setSelectedPlanId}
              salePhase={saleWindow.phase}
              countdownLabel={countdownLabel}
            />
            <button
              className="upgrade-btn"
              disabled={upgrading}
              onClick={() => handleUpgrade(selectedPlanId)}
            >
              {upgrading ? "Starting checkout…" : selectedPlan?.ctaLabel || "Upgrade Now"}
            </button>
            <p className="upgrade-footnote">Secure checkout via Stripe. No hidden fees or rebilling.</p>
          </div>
        )}

      {/* Stripe Connect Section - For users without Stripe */}
      {!business?.stripeConnected && (
        <div className="stripe-connect-section" style={{ 
          marginBottom: 24, 
          padding: 20, 
          border: "1px solid var(--sys-chip-strong-border)", 
          borderRadius: 8, 
          textAlign: "center",
          background: "var(--sys-surface)"
        }}>
          <h3 style={{ color: "var(--sys-primary)", marginBottom: 12 }}>💳 Connect Stripe Payments</h3>
          <p style={{ marginBottom: 16, color: "var(--sys-text-muted)" }}>
            Accept card payments directly on your menu.<br />
            Secure setup with Stripe Express - takes 2 minutes.
          </p>
          <button
            className="stripe-connect-btn"
            onClick={async () => {
              try {
                setSaving(true);
                const token = await getAuthToken();
                
                const formData = new FormData();
                formData.append("businessId", business.businessId);
                formData.append("name", form.name);
                formData.append("email", form.email);
                formData.append("phone", "");
                formData.append("logoUrl", logoUrl);
                formData.append("openingHours", JSON.stringify(form.openingHours));
                formData.append("timeZone", form.timeZone || DEFAULT_TIME_ZONE);
                formData.append("primaryColor", form.primaryColor);
                formData.append("secondaryColor", form.secondaryColor);
                formData.append("businessFont", form.businessFont);
                formData.append("addressLine1", form.addressLine1.trim());
                formData.append("addressLine2", form.addressLine2.trim());
                formData.append("addressCity", form.addressCity.trim());
                formData.append("addressPostcode", form.addressPostcode.trim());
                formData.append("addressCountry", (form.addressCountry || "United Kingdom").trim());
                formData.append("currencyCode", (form.currencyCode || DEFAULT_CURRENCY_CODE).trim());
                formData.append("digitalProductUrl", pdfUrl || "");
                formData.append("pdfUrl", pdfUrl || "");
                formData.append("imageUrls", JSON.stringify(imageUrls || []));
                formData.append("bookingSchedules", JSON.stringify(bookingSchedules || []));
                formData.append("planType", business.plan || business.planType || "free");
                formData.append("onboardingComplete", business?.onboardingComplete === false ? "false" : "true");
                formData.append("stripeConnected", "true"); // This triggers Stripe onboarding
                formData.append("returnUrl", "https://www.brandmykitchen.com/settings?stripe=complete");
                formData.append("refreshUrl", "https://www.brandmykitchen.com/settings?stripe=refresh");
                
                const res = await fetch(BUSINESS_API_URL, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` },
                  body: formData,
                });
                
                if (!res.ok) throw new Error("Failed to connect Stripe");
                const data = await res.json();
                
                if (data.onboardingUrl) {
                  window.location.href = data.onboardingUrl;
                } else {
                  alert("Could not start Stripe setup. Please try again.");
                }
              } catch (err) {
                console.error("Stripe setup error:", err);
                alert("Stripe setup failed. Please try again.");
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? "Connecting..." : "Connect Stripe Account"}
          </button>
          <div style={{ fontSize: "12px", color: "var(--sys-text-muted)", marginTop: 8 }}>
            Stripe handles all payment processing securely. No card details stored on our servers.
          </div>
        </div>
      )}

      {/* Stripe Dashboard Section - For users with Stripe connected */}
      {business?.stripeConnected && business?.stripeAccountId && (
        <div className="stripe-dashboard-section" style={{ 
          marginBottom: 24, 
          padding: 20, 
          border: "1px solid var(--color-success)", 
          borderRadius: 8, 
          textAlign: "center",
          background: "var(--sys-surface)"
        }}>
          <h3 style={{ color: "var(--color-success)", marginBottom: 12 }}>✅ Stripe Connected</h3>
          <p style={{ marginBottom: 16, color: "var(--sys-text-muted)" }}>
            Account ID: <code>{business.stripeAccountId}</code><br />
            Manage your Stripe account, view payouts, and update banking details.
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              className="stripe-dashboard-btn"
              style={{
                background: "var(--sys-primary)",
                color: "var(--sys-text-strong)", 
                padding: "12px 20px",
                borderRadius: 6,
                border: "1px solid var(--sys-chip-strong-border)",
                fontWeight: "600",
                cursor: "pointer",
                opacity: stripeLoginLoading ? 0.8 : 1,
              }}
              onClick={async () => {
                try {
                  setStripeLoginLoading(true);
                  const token = await getAuthToken();
                  const res = await fetch(`${BUSINESS_API_URL}?action=stripe-login-link`, {
                    method: "GET",
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) {
                    let message = "Could not open Stripe dashboard.";
                    try {
                      const err = await res.json();
                      message = err?.error || message;
                    } catch (_ignore) {
                      // ignore JSON parse issues
                    }
                    throw new Error(message);
                  }
                  const data = await res.json();
                  if (data?.loginUrl) {
                    window.open(data.loginUrl, "_blank", "noopener");
                  } else {
                    throw new Error("Stripe login link missing from response.");
                  }
                } catch (err) {
                  console.error("Stripe dashboard link error:", err);
                  alert(err?.message || "Could not open Stripe dashboard. Please try again.");
                } finally {
                  setStripeLoginLoading(false);
                }
              }}
              disabled={stripeLoginLoading}
            >
              {stripeLoginLoading ? "Opening..." : "Open Stripe Dashboard"}
            </button>
            <button
              className="stripe-onboarding-btn"
              style={{
                background: "var(--color-warning)",
                color: "var(--sys-text-strong)", 
                padding: "12px 20px",
                borderRadius: 6,
                border: "1px solid var(--color-warning-strong)",
                fontWeight: "600",
                cursor: "pointer"
              }}
              onClick={async () => {
                try {
                  setSaving(true);
                  const token = await getAuthToken();
                  
                  const formData = new FormData();
                  formData.append("businessId", business.businessId);
                  formData.append("name", form.name);
                  formData.append("email", form.email);
                  formData.append("phone", "");
                  formData.append("logoUrl", logoUrl);
                  formData.append("openingHours", JSON.stringify(form.openingHours));
                  formData.append("timeZone", form.timeZone || DEFAULT_TIME_ZONE);
                  formData.append("primaryColor", form.primaryColor);
                  formData.append("secondaryColor", form.secondaryColor);
                  formData.append("businessFont", form.businessFont);
                  formData.append("addressLine1", form.addressLine1.trim());
                   formData.append("addressLine2", form.addressLine2.trim());
                   formData.append("addressCity", form.addressCity.trim());
                   formData.append("addressPostcode", form.addressPostcode.trim());
                formData.append("addressCountry", (form.addressCountry || "United Kingdom").trim());
                formData.append("currencyCode", (form.currencyCode || DEFAULT_CURRENCY_CODE).trim());
                formData.append("digitalProductUrl", pdfUrl || "");
                formData.append("pdfUrl", pdfUrl || "");
                formData.append("imageUrls", JSON.stringify(imageUrls || []));
                formData.append("bookingSchedules", JSON.stringify(bookingSchedules || []));
                formData.append("planType", business.plan || business.planType || "free");
                formData.append("stripeConnected", "true"); // Re-trigger onboarding
                  
                  const res = await fetch(BUSINESS_API_URL, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                  });
                  
                  if (!res.ok) throw new Error("Failed to restart Stripe setup");
                  const data = await res.json();
                  
                  if (data.onboardingUrl) {
                    window.location.href = data.onboardingUrl;
                  } else {
                    alert("Could not restart Stripe setup. Please contact support.");
                  }
                } catch (err) {
                  console.error("Stripe restart error:", err);
                  alert("Failed to restart Stripe setup. Please try again.");
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              {saving ? "Restarting..." : "Complete Setup"}
            </button>
          </div>
          <div style={{ fontSize: "12px", color: "var(--sys-text-muted)", marginTop: 8 }}>
            If you haven't completed your Stripe verification, click "Complete Setup" to finish the process.
          </div>
        </div>
      )}

      <form className="settings-form" onSubmit={handleSave}>
        <label>
          Business Name
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Contact Email
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Address line 1
          <input
            type="text"
            name="addressLine1"
            value={form.addressLine1}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Address line 2 (optional)
          <input
            type="text"
            name="addressLine2"
            value={form.addressLine2}
            onChange={handleChange}
          />
        </label>
        <label>
          City
          <input
            type="text"
            name="addressCity"
            value={form.addressCity}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Postcode
          <input
            type="text"
            name="addressPostcode"
            value={form.addressPostcode}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Country
          <input
            type="text"
            name="addressCountry"
            value={form.addressCountry}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Time Zone
          <select
            name="timeZone"
            value={form.timeZone}
            onChange={handleChange}
            required
          >
            {timezoneOptions.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </label>
        {/* Store Online feature removed */}
        <div className="opening-hours-group">
          <label>Opening Hours</label>
          {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map(day => (
            <div key={day} style={{ marginBottom: 6 }}>
              <span style={{ width: 80, display: "inline-block" }}>{day.charAt(0).toUpperCase() + day.slice(1)}:</span>
              <input
                type="text"
                value={form.openingHours[day] || ""}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    openingHours: { ...prev.openingHours, [day]: e.target.value }
                  }))
                }
                placeholder="e.g. 09:00-17:00 or closed"
                style={{ width: 120 }}
              />
            </div>
          ))}
        </div>
        <div className="color-picker-row">
          <label>
            Primary Colour
            <input
              type="color"
              name="primaryColor"
              value={form.primaryColor}
              onChange={handleChange}
              className="color-input"
            />
            <span className="color-value">{form.primaryColor}</span>
          </label>
          <label>
            Secondary Colour
            <input
              type="color"
              name="secondaryColor"
              value={form.secondaryColor}
              onChange={handleChange}
              className="color-input"
            />
            <span className="color-value">{form.secondaryColor}</span>
          </label>
        </div>
        <div className="font-picker-row">
          <label>
            Business Font
            <select
              name="businessFont"
              value={form.businessFont}
              onChange={handleChange}
              className="font-select"
            >
              {FONT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Currency
          <select
            name="currencyCode"
            value={form.currencyCode}
            onChange={handleChange}
            required
          >
            {currencyOptions.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
          <span style={{ display: "block", fontSize: 12, color: "#4b5563", marginTop: 4 }}>
            Prices show as {currencySymbol} ({(form.currencyCode || DEFAULT_CURRENCY_CODE).toUpperCase()})
          </span>
        </label>
        {SHOW_BOOKING_BETA && (
        <div className="schedules-section">
          <div className="schedules-header">
            <div>
              <h3>Booking Schedules</h3>
              <p className="schedules-subtitle">
                Add resources and recurring availability. We’ll generate slots and keep them in sync.
              </p>
            </div>
            <span className="badge">Beta</span>
          </div>
          <div className="schedule-form">
            <div className="schedule-grid">
              <label>
                Resource name
                <input
                  type="text"
                  value={scheduleDraft.name}
                  onChange={(e) => updateScheduleDraft("name", e.target.value)}
                  placeholder="e.g. Studio A"
                />
              </label>
              <label>
                Resource ID (optional)
                <input
                  type="text"
                  value={scheduleDraft.resourceId}
                  onChange={(e) => updateScheduleDraft("resourceId", e.target.value)}
                  placeholder="auto if blank"
                />
              </label>
              <label>
                Timezone
                <select
                  value={scheduleDraft.timezone}
                  onChange={(e) => updateScheduleDraft("timezone", e.target.value)}
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </label>
              <label>
                Start date (optional)
                <input
                  type="text"
                  value={scheduleDraft.startDate}
                  onChange={(e) => updateScheduleDraft("startDate", e.target.value)}
                  placeholder="YYYY-MM-DD"
                />
              </label>
              <label>
                End date (optional)
                <input
                  type="text"
                  value={scheduleDraft.endDate}
                  onChange={(e) => updateScheduleDraft("endDate", e.target.value)}
                  placeholder="YYYY-MM-DD"
                />
              </label>
            </div>

            <div className="days-row">
              <span>Days of week</span>
              <div className="chips">
                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((label, idx) => (
                  <button
                    key={label}
                    type="button"
                    className={`chip ${scheduleDraft.daysOfWeek.includes(idx) ? "active" : ""}`}
                    onClick={() => toggleDay(idx)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="schedule-grid">
              <label>
                Daily start
                <input
                  type="text"
                  value={scheduleDraft.dailyStartTime}
                  onChange={(e) => updateScheduleDraft("dailyStartTime", e.target.value)}
                  placeholder="09:00"
                />
              </label>
              <label>
                Daily end
                <input
                  type="text"
                  value={scheduleDraft.dailyEndTime}
                  onChange={(e) => updateScheduleDraft("dailyEndTime", e.target.value)}
                  placeholder="17:00"
                />
              </label>
              <label>
                Slot duration (mins)
                <input
                  type="number"
                  value={scheduleDraft.slotDurationMinutes}
                  onChange={(e) => updateScheduleDraft("slotDurationMinutes", Number(e.target.value))}
                  min={5}
                  step={5}
                />
              </label>
              <label>
                Interval (mins)
                <input
                  type="number"
                  value={scheduleDraft.intervalMinutes}
                  onChange={(e) => updateScheduleDraft("intervalMinutes", Number(e.target.value))}
                  min={5}
                  step={5}
                />
              </label>
              <label>
                Buffer (mins)
                <input
                  type="number"
                  value={scheduleDraft.bufferMinutes}
                  onChange={(e) => updateScheduleDraft("bufferMinutes", Number(e.target.value))}
                  min={0}
                  step={5}
                />
              </label>
              <label>
                Capacity per slot
                <input
                  type="number"
                  value={scheduleDraft.maxBookings}
                  onChange={(e) => updateScheduleDraft("maxBookings", Number(e.target.value))}
                  min={1}
                />
              </label>
              <label>
                Price amount (optional)
                <input
                  type="text"
                  value={scheduleDraft.price?.amount || ""}
                  onChange={(e) =>
                    updateScheduleDraft("price", {
                      ...scheduleDraft.price,
                      amount: e.target.value,
                    })
                  }
                  placeholder="e.g. 40.00"
                />
              </label>
              <label>
                Price currency
                <select
                  value={scheduleDraft.price?.currencyCode || form.currencyCode}
                  onChange={(e) =>
                    updateScheduleDraft("price", {
                      ...scheduleDraft.price,
                      currencyCode: e.target.value,
                    })
                  }
                >
                  {currencyOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Blackout dates
                <input
                  type="text"
                  value={scheduleDraft.blackoutDates.join(",")}
                  onChange={(e) =>
                    updateScheduleDraft("blackoutDates", e.target.value.split(",").map((d) => d.trim()).filter(Boolean))
                  }
                  placeholder="YYYY-MM-DD, YYYY-MM-DD"
                />
              </label>
              <label className="full-width">
                Notes (optional)
                <input
                  type="text"
                  value={scheduleDraft.notes}
                  onChange={(e) => updateScheduleDraft("notes", e.target.value)}
                  placeholder="What guests should know"
                />
              </label>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={scheduleDraft.enabled}
                  onChange={(e) => updateScheduleDraft("enabled", e.target.checked)}
                />
                <span>Enabled</span>
              </label>
            </div>
            <div className="schedule-actions">
              <button type="button" className="secondary" onClick={() => {
                setEditingScheduleId(null);
                setScheduleDraft({
                  ...blankSchedule,
                  timezone: form.timeZone || DEFAULT_TIME_ZONE,
                  price: { ...blankSchedule.price, currencyCode: form.currencyCode || DEFAULT_CURRENCY_CODE },
                });
              }}>
                Reset
              </button>
              <button type="button" className="primary" onClick={saveScheduleDraft}>
                {editingScheduleId ? "Update schedule" : "Add schedule"}
              </button>
            </div>
          </div>

          <div className="schedules-list">
            {bookingSchedules.length === 0 && (
              <div className="empty-state">No schedules yet. Add your first resource above.</div>
            )}
            {bookingSchedules
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((sched) => (
                <div key={sched.scheduleId} className="schedule-card">
                  <div className="schedule-card-head">
                    <div>
                      <div className="schedule-title">
                        {sched.name || "Untitled"}{" "}
                        {!sched.enabled && <span className="badge muted">Disabled</span>}
                      </div>
                      <div className="schedule-meta">
                        {sched.dailyStartTime}–{sched.dailyEndTime} · {sched.daysOfWeek?.length || 0} days · {sched.slotDurationMinutes}m slots · cap {sched.maxBookings}
                      </div>
                    </div>
                    <div className="schedule-actions-inline">
                      <button type="button" onClick={() => editSchedule(sched.scheduleId)}>Edit</button>
                      <button type="button" className="danger" onClick={() => deleteSchedule(sched.scheduleId)}>Delete</button>
                    </div>
                  </div>
                  {sched.notes ? <div className="schedule-notes">{sched.notes}</div> : null}
                  <div className="schedule-tags">
                    {sched.timezone && <span className="tag">TZ {sched.timezone}</span>}
                    {sched.price?.amount && <span className="tag">Price {sched.price.amount} {sched.price.currencyCode || form.currencyCode}</span>}
                    {sched.blackoutDates?.length ? (
                      <span className="tag">Blackouts {sched.blackoutDates.length}</span>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
        </div>
        )}
        <label>
          Business Logo
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
          />
        </label>
        {(logoPreview || logoUrl) && (
          <div className="settings-logo-preview">
            <img src={logoPreview || logoUrl} alt="Logo preview" />
          </div>
        )}
        <label>
          Hero Image
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleDedicatedImageChange(e, "hero")}
            disabled={heroImageBusy}
          />
          <small className="settings-field-hint">
            Main booking-page image. Best as a wide lifestyle shot, around 1600x900 to 1800x1200.
          </small>
          {(heroImagePreview || heroImageUrl) ? (
            <div className="settings-media-preview settings-media-preview--hero">
              <img src={heroImagePreview || heroImageUrl} alt="Hero preview" />
              <button type="button" className="settings-inline-action" onClick={() => { setHeroImageUrl(""); setHeroImagePreview(""); }}>Remove hero image</button>
            </div>
          ) : null}
          {heroImageBusy ? <small style={{ display: "block", marginTop: 4 }}>Uploading hero image…</small> : null}
        </label>
        <label>
          Venue Image
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleDedicatedImageChange(e, "venue")}
            disabled={venueImageBusy}
          />
          <small className="settings-field-hint">
            Optional venue photo for the location section. Best as a clean 4:3 or landscape image around 1200x900 to 1600x1200.
          </small>
          {(venueImagePreview || venueImageUrl) ? (
            <div className="settings-media-preview settings-media-preview--venue">
              <img src={venueImagePreview || venueImageUrl} alt="Venue preview" />
              <button type="button" className="settings-inline-action" onClick={() => { setVenueImageUrl(""); setVenueImagePreview(""); }}>Remove venue image</button>
            </div>
          ) : null}
          {venueImageBusy ? <small style={{ display: "block", marginTop: 4 }}>Uploading venue image…</small> : null}
        </label>
        <label>
          Venue Name
          <input
            type="text"
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
            placeholder="Coppa Club Cobham"
          />
          <small className="settings-field-hint">Shown in the booking page location block, for example Coppa Club Cobham.</small>
        </label>
        <label>
          Map URL
          <input
            type="url"
            value={mapUrl}
            onChange={(e) => setMapUrl(e.target.value)}
            placeholder="https://maps.google.com/..."
          />
          <small className="settings-field-hint">Optional. If added, the booking page shows an “Open in Maps” link.</small>
        </label>
        <label>
          Gallery Images (max 3, up to 8 MB each before optimisation)
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            disabled={imageBusy || imageUrls.length >= 3}
          />
          {imageUrls.length > 0 && (
            <div className="settings-logo-preview" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {imageUrls.map((url, idx) => (
                <img key={url} src={url} alt={`Gallery ${idx + 1}`} style={{ width: 80, height: 80, objectFit: "cover" }} />
              ))}
            </div>
          )}
          {imageBusy && (
            <small style={{ display: "block", marginTop: 4 }}>Uploading…</small>
          )}
        </label>
        <label>
          Digital Product (PDF, max 200 KB)
          <input
            type="file"
            accept="application/pdf"
          onChange={handlePdfChange}
          />
          {(pdfUrl || pdfFile) && (
            <small style={{ display: "block", marginTop: 4 }}>
              {pdfFile ? pdfFile.name : "Current PDF:"}{" "}
              {pdfViewUrl ? (
                <a href={pdfViewUrl} target="_blank" rel="noreferrer">
                  View PDF
                </a>
              ) : (
                <button
                  type="button"
                  style={{ border: "none", background: "none", padding: 0, color: "var(--sys-primary)", cursor: "pointer" }}
                  onClick={() => fetchPdfViewUrl()}
                >
                  Get view link
                </button>
              )}
            </small>
          )}
        </label>
        {error && (
          <div style={{ color: "red", marginTop: 8 }}>{error}</div>
        )}
        <button className="settings-save-btn" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>
    </div>
    </div>
  );
}
